import { useState } from "react";
import { X, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const strength = (() => {
    if (!next) return { score: 0, label: "—", color: "#64748b" };
    let s = 0;
    if (next.length >= 8) s++;
    if (/[A-Z]/.test(next)) s++;
    if (/[0-9]/.test(next)) s++;
    if (/[^A-Za-z0-9]/.test(next)) s++;
    if (next.length >= 12) s++;
    const labels = ["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte", "Excelente"];
    const colors = ["#dc2626", "#ef4444", "#d97706", "#f59e0b", "#16a34a", "#10b981"];
    return { score: s, label: labels[s], color: colors[s] };
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (next !== confirm) {
      toast.error("La confirmación no coincide con la nueva contraseña");
      return;
    }
    if (current === next) {
      toast.error("La nueva contraseña debe ser distinta a la actual");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Contraseña actualizada correctamente");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="change-password-modal"
      className="fixed inset-0 z-[100] p-4 flex items-center justify-center"
      style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-y-auto rounded-md shadow-2xl"
        style={{
          background: "#0a0f1d",
          border: "1px solid var(--border-2)",
          maxHeight: "calc(100dvh - 2rem)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 flex items-center justify-between sticky top-0 z-10"
          style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-sm flex items-center justify-center"
              style={{ background: "rgba(34,211,238,0.12)", border: "1px solid rgba(34,211,238,0.35)" }}
            >
              <KeyRound className="w-4 h-4 text-cyan-300" />
            </div>
            <div>
              <div className="label-mono" style={{ color: "var(--cyan)" }}>// SEGURIDAD</div>
              <h2 className="font-display font-bold text-lg text-slate-100 tracking-wide">Cambiar contraseña</h2>
            </div>
          </div>
          <button
            data-testid="change-password-close"
            onClick={onClose}
            className="text-slate-500 hover:text-cyan-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="label-mono block mb-1.5">Contraseña actual</label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="change-password-current"
                className="w-full input-cyber px-3 py-2 pr-10 text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="label-mono block mb-1.5">Nueva contraseña</label>
            <div className="relative">
              <input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="change-password-new"
                className="w-full input-cyber px-3 py-2 pr-10 text-sm"
                placeholder="Mínimo 6 caracteres"
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300"
                tabIndex={-1}
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {next && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded" style={{ background: "#1e293b" }}>
                  <div
                    className="h-full rounded transition-all"
                    style={{
                      width: `${(strength.score / 5) * 100}%`,
                      background: strength.color,
                    }}
                  />
                </div>
                <span
                  className="label-mono tabular-nums text-[10px]"
                  style={{ color: strength.color }}
                  data-testid="change-password-strength"
                >
                  {strength.label}
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="label-mono block mb-1.5">Confirmar nueva contraseña</label>
            <input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              data-testid="change-password-confirm"
              className="w-full input-cyber px-3 py-2 text-sm"
              placeholder="Repite tu nueva contraseña"
            />
            {confirm && next && confirm !== next && (
              <div className="text-xs mt-1" style={{ color: "#dc2626" }}>
                Las contraseñas no coinciden
              </div>
            )}
          </div>

          <div
            className="text-[11px] text-slate-400 px-3 py-2 rounded-sm"
            style={{ background: "rgba(34,211,238,0.05)", border: "1px solid rgba(34,211,238,0.18)" }}
          >
            Recomendado: usa al menos 8 caracteres con mayúsculas, números y un símbolo.
          </div>

          <div
            className="flex gap-2 justify-end pt-3 -mx-6 -mb-6 px-6 pb-6 sticky bottom-0 z-20"
            style={{
              borderTop: "1px solid var(--border)",
              background: "#0a0f1d",
              paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="change-password-cancel"
              className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="change-password-submit"
              className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm"
            >
              {submitting ? "Guardando…" : "Actualizar contraseña"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
